import { memo, useMemo } from 'react'

import styled from 'styled-components'

import { BlockGroup as BlockGroupType } from '../types/blueprint'

import Block from './Block'
import { shouldRenderBlockOrGroup } from './utils/conditionsHelpers'

import { PreventPageBreak } from '@/components/shared-components/PrintMedia'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { useIntervalWatch } from '@/hooks/useIntervalWatch'

type WrapperProps = {
  background: string
  padding: string
  bordered: boolean
  noMargin?: boolean
}

const Wrapper = styled(PreventPageBreak)<WrapperProps>`
  background: ${({ background }) => background};
  padding: ${({ padding }) => padding};
  border: ${({ bordered, theme }) =>
    bordered ? `1px solid ${theme.colour.neutral.border}` : 'none'};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '1.5rem')};

  ${({ theme, noMargin }) => theme.media.md`
    margin-bottom: ${noMargin ? '0' : '3rem'};
  `}
`

type Props = {
  blockGroup: BlockGroupType
  index: number
  sectionId: string
  noMargin?: boolean
}

const BlockGroup: React.FC<Props> = memo(
  ({ blockGroup, index, sectionId, noMargin }) => {
    const { renderOptions } = useTemplateRenderer()

    const blockGroupHasConditions = useMemo(
      () =>
        blockGroup.conditions?.flatMap((group) =>
          group?.map((condition) => condition.blockId)
        )?.length,
      [blockGroup.conditions]
    )

    if (renderOptions?.renderingInPdf && blockGroup.hideFromPdf) {
      return null
    }

    if (blockGroupHasConditions) {
      return (
        <InternalBlockGroupIfConditions
          index={index}
          blockGroup={blockGroup}
          sectionId={sectionId}
          noMargin={noMargin}
        />
      )
    }

    return (
      <InternalBlockGroupIfNoConditions
        index={index}
        blockGroup={blockGroup}
        sectionId={sectionId}
        noMargin={noMargin}
      />
    )
  }
)

const InternalBlockGroupIfConditions: React.FC<Props> = memo(
  ({ blockGroup, sectionId, noMargin }) => {
    const { renderOptions, mode, form, config } = useTemplateRenderer()

    const fieldsToWatch = useMemo(
      () =>
        blockGroup.conditions?.flatMap((group) =>
          group?.map((condition) => condition.blockId)
        ) ?? [],
      [blockGroup.conditions]
    )
    const watchedValues = useIntervalWatch(fieldsToWatch, form)

    if (
      !shouldRenderBlockOrGroup({
        blockOrGroup: blockGroup,
        mode,
        formData: watchedValues,
        isPublic: config?.isPublic,
      })
    ) {
      return null
    }

    return (
      <Wrapper
        shouldAvoidPageBreak={renderOptions.preventPageBreakInsideBlockGroup}
        background={renderOptions.blockGroupBackground}
        bordered={renderOptions.blockGroupBordered}
        padding={
          renderOptions.blockGroupBordered
            ? renderOptions.blockGroupPadding
            : '0'
        }
        noMargin={noMargin}
      >
        {blockGroup.blocks?.map((block, i) => (
          <Block
            key={i}
            block={block}
            index={i}
            blockGroup={blockGroup}
            sectionId={sectionId}
          />
        ))}
      </Wrapper>
    )
  }
)

const InternalBlockGroupIfNoConditions: React.FC<Props> = memo(
  ({ blockGroup, sectionId, noMargin }) => {
    const { renderOptions } = useTemplateRenderer()

    return (
      <Wrapper
        shouldAvoidPageBreak={renderOptions.preventPageBreakInsideBlockGroup}
        background={renderOptions.blockGroupBackground}
        bordered={renderOptions.blockGroupBordered}
        padding={
          renderOptions.blockGroupBordered
            ? renderOptions.blockGroupPadding
            : '0'
        }
        noMargin={noMargin}
      >
        {blockGroup.blocks?.map((block, i) => (
          <Block
            key={i}
            block={block}
            index={i}
            blockGroup={blockGroup}
            sectionId={sectionId}
          />
        ))}
      </Wrapper>
    )
  }
)

export default BlockGroup
